/**
 * Shared API Service
 * Following Feature-Based Modular Architecture
 */

import {
  accountRefusalCodeOf,
  rememberSignedOutReason,
} from '@/platform/auth/services/account-refusal'
import { isSessionEndCode, type SessionEndCode } from '@/platform/auth/services/session-end-reason'
import { emitSessionExpired } from '@/platform/auth/services/session-events'
import {
  emitMaintenance,
  isMaintenanceRefusal,
  noticeFromRefusal,
} from '@/platform/maintenance/maintenance-events'
import { requestStepUp, type StepUpCredential } from '@/platform/step-up/step-up-controller'
import { emitImpersonationRejected } from '@/shared/services/impersonation-events'
import {
  clearActiveImpersonationSession,
  getActiveImpersonationToken,
} from '@/shared/services/impersonation-session'
import { storageService } from '@/shared/services/storage.service'
import { emitAcceptedWrite } from '@/platform/api/accepted-writes'
import { ApiResponse, ApiError, ApiException, handleApiError } from '@/shared/utils/api'
import { API_BASE_URL, API_TIMEOUT, STORAGE_KEYS } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

import {
  clearAuthDataLocally,
  isAuthEndpointPathThatMustNotTriggerRefresh,
  isRejectedCredentialResponse,
  performAccessTokenRefreshAgainstBackend,
  type RefreshResult,
} from './api-refresh'

const STEP_UP_ENDPOINT = '/api/auth/step-up'

function isStepUpEndpoint(endpoint: string): boolean {
  return (endpoint.split('?')[0] ?? endpoint).replace(/\/$/, '') === STEP_UP_ENDPOINT
}

/** Is this the `{ error: { code: 'STEP_UP_REQUIRED' } }` body of a stale proof of identity? */
function isStepUpRequiredBody(body: unknown): boolean {
  return (body as { error?: { code?: unknown } } | null)?.error?.code === 'STEP_UP_REQUIRED'
}

/** Wait before the single retry of a transient refresh failure when the server sent no `Retry-After`. */
const TRANSIENT_REFRESH_DEFAULT_WAIT_SECONDS = 2
/** Upper bound for that wait: a hostile or mistaken `Retry-After` must never park every request for minutes. */
const TRANSIENT_REFRESH_MAX_WAIT_SECONDS = 10

/**
 * The app's active language for the `Accept-Language` header, so the backend
 * localizes error/success messages to the user's chosen language. Mirrors the
 * helper in api-helper.ts (localStorage → browser → 'en'). Applied to EVERY
 * request centrally so services that call apiService directly (not the
 * api-helper wrapper) still send the locale — e.g. change-password, whose 4xx
 * message used to come back in English regardless of the UI language.
 */
function getAcceptLanguageHeader(): string {
  if (typeof window === 'undefined') return 'en'
  const stored = localStorage.getItem(STORAGE_KEYS.LANGUAGE)
  if (stored) return stored
  const browserLocale = navigator.language.split('-')[0]
  return browserLocale && ['en', 'es'].includes(browserLocale) ? browserLocale : 'en'
}

// ============================================================================
// Compatibility types — mirrors the shape that api-helper used to expose.
// Consumers that previously imported from api-helper or useApi expect these.
// ============================================================================

export interface ApiHelperResponse<T = unknown> {
  success: boolean
  data?: T
  message?: string
  error?: {
    code: string
    message: string
    statusCode: number
    /**
     * Either Zod field-error array (current pattern) OR a structured
     * context object propagated from the backend's error envelope
     * (`{ success: false, error: { code, message, context } }`). The
     * helper used to drop non-array details to undefined, which silently
     * lost rich rejection metadata like `instructionsUrl` / `videoUrl`
     * that the WRONG_FILE_FORMAT screen needs.
     */
    details?: Array<{ field: string; message: string; code?: string }> | Record<string, unknown>
  }
}

export interface ApiCallOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: unknown
  headers?: Record<string, string>
  params?: Record<string, string | number | boolean | null | undefined>
  skipAuth?: boolean
}

/**
 * One signal that aborts when any of the given ones does: `AbortSignal.any` where the browser has it (Chrome 116+,
 * Safari 17.4+, Firefox 124+) and a combined controller elsewhere (it would otherwise throw a TypeError that
 * `makeRequest` reports as a network failure).
 */
function anySignal(signals: readonly AbortSignal[]): AbortSignal {
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([...signals])
  const controller = new AbortController()
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort()
      break
    }
    signal.addEventListener('abort', () => controller.abort(), { once: true })
  }
  return controller.signal
}

interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  headers?: Record<string, string>
  timeout?: number
  body?: unknown
  /** An external cancellation (React Query's `signal`): aborting it aborts the fetch; `request` rejects with `AbortError`. */
  signal?: AbortSignal
  /** `'blob'` answers a file (a download): no JSON is read, `data` is `{ blob, contentType, fileName }`. */
  responseType?: 'json' | 'blob'
}

/** The JSON body of a response, or null when it has none (204, another content type) or does not parse. */
async function readJsonBody(response: Response, label: string) {
  const contentType = response.headers.get('content-type')
  if (response.status === 204 || !contentType?.includes('application/json')) {
    return null
  }
  try {
    return await response.json()
  } catch (error) {
    logger.warn(`[API] Failed to parse ${label}, using null:`, error)
    return null
  }
}

/** The file a download answered: the blob and what its headers said (`Content-Disposition` names the file). */
async function readFileBody(response: Response) {
  const disposition = response.headers.get('content-disposition') ?? ''
  return {
    blob: await response.blob(),
    contentType: response.headers.get('content-type') ?? '',
    fileName: /filename="([^"]+)"/.exec(disposition)?.[1] ?? null,
  }
}

/**
 * ⚠️⚠️ `TOKEN_REVOKED`: every session of the user was closed on purpose (an
 * administrator's «Cerrar todas las sesiones», a password or role change). The
 * app said «Tu sesión ha caducado» — measured in e2e 2026-09-27 — which tells a
 * user whose laptop was just locked out nothing. Carry the code to the toast.
 * Same for a deleted account (`ACCOUNT_UNAVAILABLE`) and a moved or re-roled
 * user (`SESSION_OUTDATED`), measured in e2e 2026-09-27.
 */
function revokedSessionsReason(body: unknown): SessionEndCode | undefined {
  const code = (body as { error?: { code?: unknown } } | null)?.error?.code
  return isSessionEndCode(code) ? code : undefined
}

class ApiService {
  private baseURL: string
  private defaultTimeout: number
  private defaultHeaders: Record<string, string>

  /**
   * Single-flight refresh promise. While a refresh is in progress, all other
   * 401-handling callers await this same promise instead of dispatching
   * their own `/auth/refresh` request — preventing a thundering herd when
   * many concurrent requests hit 401 simultaneously.
   */
  private refreshPromise: Promise<RefreshResult> | null = null

  constructor() {
    this.baseURL = API_BASE_URL
    this.defaultTimeout = API_TIMEOUT
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    }
  }

  setAuthToken(token: string): void {
    this.defaultHeaders['Authorization'] = `Bearer ${token}`
  }

  removeAuthToken(): void {
    delete this.defaultHeaders['Authorization']
  }

  setBaseURL(url: string): void {
    this.baseURL = url
  }

  /**
   * Single-flight refresh: if a refresh is already in progress, return the
   * existing promise. Otherwise start a new one and clear the slot when done
   * (success OR failure). The one retry of a TRANSIENT failure (429, 5xx,
   * network) happens INSIDE the flight, so concurrent requests keep sharing it
   * and the backend sees one renewal call per attempt, not one per request.
   *
   * Returns the classified outcome: only `rejected` means the session is dead.
   */
  private async refreshAccessTokenSingleFlight(): Promise<RefreshResult> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }
    this.refreshPromise = this.refreshWithOneTransientRetry().finally(() => {
      this.refreshPromise = null
    })
    return this.refreshPromise
  }

  private async refreshWithOneTransientRetry(): Promise<RefreshResult> {
    let result = await performAccessTokenRefreshAgainstBackend(this.baseURL)
    if (result.kind === 'transient') {
      const waitSeconds = Math.min(
        result.retryAfterSeconds ?? TRANSIENT_REFRESH_DEFAULT_WAIT_SECONDS,
        TRANSIENT_REFRESH_MAX_WAIT_SECONDS,
      )
      logger.warn(
        `[API] Token refresh could not be completed now; retrying once in ${waitSeconds}s`,
      )
      await new Promise<void>((resolve) => setTimeout(resolve, waitSeconds * 1000))
      result = await performAccessTokenRefreshAgainstBackend(this.baseURL)
    }
    // Sync the in-memory bearer header with the new token.
    // `performAccessTokenRefreshAgainstBackend` only writes to storage,
    // but the retry path in `makeRequest` reads `this.defaultHeaders`
    // first and only falls back to storage when no Authorization header
    // is present. After login, `defaultHeaders` holds the (now stale)
    // bearer, so without this explicit sync the retry would reuse it
    // and 401 again — the refresh would silently fail to recover.
    if (result.kind === 'ok') {
      this.setAuthToken(result.accessToken)
    }
    return result
  }

  /**
   * Renews the session after a 401 and says whether the original request
   * should be re-sent. A `rejected` renewal ends the session (local wipe +
   * `emitSessionExpired`, the toast + redirect to login); a `transient` one
   * leaves token and user untouched and returns false, so the caller surfaces
   * the ORIGINAL error and the next action can try again.
   */
  private async renewSessionAfter401(body: unknown): Promise<boolean> {
    const result = await this.refreshAccessTokenSingleFlight()
    if (result.kind === 'ok') return true
    if (result.kind === 'rejected') {
      this.clearAuthLocally()
      emitSessionExpired(revokedSessionsReason(body))
    }
    return false
  }

  /**
   * Clears auth state both locally (storage) and on this instance (header).
   * Called when refresh fails — surface the original 401 to the caller after.
   */
  private clearAuthLocally(): void {
    clearAuthDataLocally()
    this.removeAuthToken()
  }

  /**
   * A 401 on a request that carried the IMPERSONATED bearer is NOT the admin's
   * own session expiring: the impersonation token died (1 h cap) or a
   * super-admin terminated the session. The refresh cookie belongs to the
   * ADMIN, so refreshing here would mint a token for a DIFFERENT identity and
   * carry on as if nothing had changed — the admin would keep reading a
   * "viewing as X" banner over their own tenant's data. Drop the impersonated
   * identity and announce it instead.
   *
   * Returns true when the 401 was consumed as an impersonation ending, so the
   * caller must NOT attempt the admin-scoped refresh.
   */
  private endImpersonationIfItsTokenWasRejected(): boolean {
    if (getActiveImpersonationToken() === null) return false
    clearActiveImpersonationSession()
    // The stale impersonated bearer must not survive on this instance either;
    // the next request falls back to the admin's own token in storage.
    this.removeAuthToken()
    emitImpersonationRejected()
    return true
  }

  /** `POST /api/auth/step-up`: resolves on 200, rejects with the ApiException (its `code`) otherwise. */
  private async submitStepUp(credential: StepUpCredential): Promise<void> {
    await this.post(STEP_UP_ENDPOINT, credential)
  }

  /** Every request's door: a write the server accepted is signalled once (`onAcceptedWrite`), retries included. */
  private async makeRequest<T>(
    endpoint: string,
    config: RequestConfig = {},
    skipAuth = false,
  ): Promise<ApiResponse<T>> {
    const response = await this.sendRequest<T>(endpoint, config, skipAuth)
    if ((config.method ?? 'GET') !== 'GET' && response.success) emitAcceptedWrite(endpoint)
    return response
  }

  private async sendRequest<T>(
    endpoint: string,
    config: RequestConfig = {},
    skipAuth = false,
    isRetryAfterRefresh = false,
    isRetryAfterStepUp = false,
  ): Promise<ApiResponse<T>> {
    const { method = 'GET', headers, timeout = this.defaultTimeout, body } = config

    const url = `${this.baseURL}${endpoint}`

    const requestHeaders: Record<string, string> = {
      // For bodyless DELETE requests, only use custom headers (don't inherit the
      // default Content-Type — Fastify rejects an empty JSON body). A DELETE WITH
      // a body (e.g. MFA disable re-auth credentials) DOES need the default
      // Content-Type so the JSON payload is parsed by the server.
      ...(method === 'DELETE' && body === undefined ? {} : this.defaultHeaders),
      // Locale for backend message localization; an explicit header still wins.
      'Accept-Language': getAcceptLanguageHeader(),
      ...(headers ?? {}),
    }

    // Filter out undefined values from headers to avoid sending them to server
    Object.keys(requestHeaders).forEach((key) => {
      if (requestHeaders[key] === undefined) {
        delete requestHeaders[key]
      }
    })

    // An ACTIVE impersonation outranks whatever bearer is sitting in
    // `defaultHeaders`. Without this override the identity a request carries
    // would be decided by provider mount order: AuthContext's
    // `initializeAuth()` re-installs the admin's own token from localStorage,
    // and it used to land AFTER the impersonation context had set the
    // impersonated one — so the app showed "viewing as X" while every request
    // went out as the admin (#2145). An explicit per-call Authorization header
    // still wins: those callers (login, refresh) mean a specific credential.
    const callerSuppliedAuthorization = (headers ?? {})['Authorization'] !== undefined
    if (!skipAuth && !callerSuppliedAuthorization) {
      const impersonationToken = getActiveImpersonationToken()
      if (impersonationToken) {
        requestHeaders['Authorization'] = `Bearer ${impersonationToken}`
      }
    }

    // Retrieve token from storage if Authorization header not present and not skipping auth
    if (!skipAuth && !requestHeaders['Authorization']) {
      const token = storageService.getAuthToken()
      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`
      }
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const requestConfig: RequestInit = {
        method,
        headers: requestHeaders,
        // The timeout and the caller's cancellation abort the same fetch.
        signal: config.signal ? anySignal([controller.signal, config.signal]) : controller.signal,
      }

      // A DELETE MAY carry a JSON body (e.g. MFA disable re-auth credentials).
      // The backend `DELETE /auth/mfa/disable` route declares a body schema and
      // reads `body.password` / `body.code`. Serialize the body for any
      // non-GET method when one is provided.
      if (body && method !== 'GET') {
        if (body instanceof FormData) {
          // FormData must be passed directly — JSON.stringify destroys it.
          // Remove Content-Type so the browser sets multipart/form-data with boundary.
          requestConfig.body = body
          delete requestHeaders['Content-Type']
          requestConfig.headers = requestHeaders
        } else {
          requestConfig.body = JSON.stringify(body)
        }
      } else if (!body && method !== 'GET' && method !== 'DELETE') {
        // Bodyless POST/PUT/PATCH (e.g. /api-config/test, /reload, /reload-all):
        // Fastify rejects empty body when Content-Type is application/json, so
        // strip the default header — same pattern as bodyless DELETE above.
        delete requestHeaders['Content-Type']
        requestConfig.headers = requestHeaders
      }

      const response = await fetch(url, requestConfig)
      clearTimeout(timeoutId)

      // A file is read as a blob below and never as JSON (a JSON export is a file too); an error is always JSON.
      const data =
        config.responseType === 'blob' && response.ok
          ? null
          : await readJsonBody(response, 'JSON response')

      if (!response.ok) {
        // Auto-refresh on 401: if the access token expired, transparently
        // refresh and retry the original request once. Skip when:
        //   - this IS already a retry (avoid infinite loop)
        //   - the caller opted out of auth (`skipAuth`)
        //   - the failing endpoint is itself an auth endpoint (login/logout/refresh)
        //   - the 401 says a CREDENTIAL was rejected (wrong password/code/passkey): that is
        //     not an expired token, and re-sending only doubles the failed attempt
        if (
          response.status === 401 &&
          !skipAuth &&
          !isRetryAfterRefresh &&
          !isAuthEndpointPathThatMustNotTriggerRefresh(endpoint) &&
          !isRejectedCredentialResponse(data) &&
          !this.endImpersonationIfItsTokenWasRejected()
        ) {
          if (await this.renewSessionAfter401(data)) {
            // Retry the original request once with the new token. The retry
            // re-reads the token from storage (set inside the refresh helper).
            return this.sendRequest<T>(endpoint, config, skipAuth, /* isRetryAfterRefresh */ true)
          }
        }

        // Sensitive action with a stale proof of identity (CAP-AC-037): ask the person to
        // confirm who they are (ONE shared modal) and re-send the request once. Not a session
        // problem: never touches the refresh path nor the logout.
        if (
          response.status === 403 &&
          !isRetryAfterStepUp &&
          !isStepUpEndpoint(endpoint) &&
          isStepUpRequiredBody(data) &&
          (await requestStepUp((credential) => this.submitStepUp(credential)))
        ) {
          return this.sendRequest<T>(
            endpoint,
            config,
            skipAuth,
            isRetryAfterRefresh,
            /* isRetryAfterStepUp */ true,
          )
        }

        // Handle backend error structure: { success: false, error: { code, message, ... } }
        // Guard against null data (e.g. unexpected 204 on error path)
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- untrusted parsed JSON: a falsy non-object `error` field must fall through to the envelope
        const errorData = data?.error || data || { message: 'Unknown error' }
        const error: ApiError = {
          status: response.status,
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string message means unset and must fall through to statusText
          message: errorData.message || data?.message || response.statusText,
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset
          code: errorData.code || data?.code,
          // NEVER fall back to the full error envelope ({ code, message, ... })
          // when the backend sends no `details`: downstream form mapping used
          // to misread the envelope keys as field names, fake a field mapping,
          // and suppress the visible global error (invalid-credentials login
          // bug). DO pass through `context` — rejection metadata such as
          // instructionsUrl/videoUrl on WRONG_FILE_FORMAT lives there, and the
          // CSV rejection screens read it from ApiException.details.
          details: errorData.details ?? errorData.context,
          ...(typeof errorData.requestId === 'string' ? { requestId: errorData.requestId } : {}),
        }
        if (isMaintenanceRefusal(error)) emitMaintenance(noticeFromRefusal(error))
        // The ACCOUNT was closed (organisation suspended / deleted, user
        // suspended): every later call would 403 too. Sign out and say why —
        // unless the refused bearer was an impersonation, which is the
        // ADMIN's session and must not be torn down with it.
        const refusal = skipAuth ? null : accountRefusalCodeOf(error)
        if (refusal && getActiveImpersonationToken() === null) {
          rememberSignedOutReason(refusal)
          this.clearAuthLocally()
          emitSessionExpired(refusal)
        }
        throw new ApiException(error)
      }

      if (config.responseType === 'blob') {
        return { data: (await readFileBody(response)) as unknown as T, success: true }
      }

      // For paginated responses, preserve the full structure (data + pagination)
      // For simple responses, extract just the data field
      const responseData = data?.pagination
        ? { data: data.data, pagination: data.pagination }
        : (data?.data ?? null)

      return {
        data: responseData,
        success: true,
        message: data?.message ?? undefined,
      }
    } catch (error) {
      clearTimeout(timeoutId)

      if (error instanceof ApiException) {
        throw error
      }

      // Timeout via AbortController
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiException({
          status: 0,
          code: 'TIMEOUT',
          message: '',
        })
      }

      // Transport/network failure (fetch() rejects with TypeError on CORS/offline/DNS)
      if (error instanceof TypeError) {
        throw new ApiException({
          status: 0,
          code: 'NETWORK_ERROR',
          message: '',
        })
      }

      const apiError = handleApiError(error)
      throw new ApiException(apiError)
    }
  }

  /**
   * Single typed door for contract calls (`callContract`): method, body and an external AbortSignal. A call the CALLER
   * cancelled rejects with the native `AbortError` (what React Query treats as a cancellation), never with the
   * `TIMEOUT` that `makeRequest` gives to any aborted fetch.
   */
  async request<T>(endpoint: string, config: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeCancellableRequest<T>(endpoint, config)
  }

  /** `makeRequest` where a call the CALLER's `config.signal` cancelled always rejects with the native `AbortError`. */
  private async makeCancellableRequest<T>(
    endpoint: string,
    config: RequestConfig,
    skipAuth = false,
  ): Promise<ApiResponse<T>> {
    const cancelled = () => new DOMException('The operation was aborted.', 'AbortError')
    let response: ApiResponse<T>
    try {
      response = await this.makeRequest<T>(endpoint, config, skipAuth)
    } catch (error) {
      throw config.signal?.aborted ? cancelled() : error
    }
    // An abort while the body was being read is swallowed by `makeRequest` (empty data): it is still a cancellation.
    if (config.signal?.aborted) throw cancelled()
    return response
  }

  async get<T>(
    endpoint: string,
    options?: {
      params?: Record<string, string | number | boolean | null | undefined>
      headers?: Record<string, string>
      /** React Query's `signal`: aborting it aborts the fetch. */
      signal?: AbortSignal
    },
    skipAuth = false,
  ): Promise<ApiResponse<T>> {
    let url = endpoint

    // Build query string if params are provided
    if (options?.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        url += (endpoint.includes('?') ? '&' : '?') + queryString
      }
    }

    const signal = options?.signal
    return this.makeCancellableRequest<T>(
      url,
      {
        method: 'GET',
        ...(options?.headers ? { headers: options.headers } : {}),
        ...(signal ? { signal } : {}),
      },
      skipAuth,
    )
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    headers?: Record<string, string>,
    skipAuth = false,
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'POST',
        body,
        ...(headers ? { headers } : {}),
      },
      skipAuth,
    )
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    headers?: Record<string, string>,
    skipAuth = false,
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'PUT',
        body,
        ...(headers ? { headers } : {}),
      },
      skipAuth,
    )
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    headers?: Record<string, string>,
    skipAuth = false,
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'PATCH',
        body,
        ...(headers ? { headers } : {}),
      },
      skipAuth,
    )
  }

  async delete<T>(
    endpoint: string,
    headers?: Record<string, string>,
    skipAuth = false,
    body?: unknown,
  ): Promise<ApiResponse<T>> {
    // Build headers object. For a bodyless DELETE we deliberately exclude the
    // default Content-Type (Fastify rejects an empty JSON body); makeRequest()
    // keeps Content-Type only when a body is present. We pass custom headers
    // through here either way.
    const deleteHeaders: Record<string, string> = {}

    // Add custom headers if provided
    if (headers) {
      Object.assign(deleteHeaders, headers)
    }

    // Forward an optional request body. Some DELETE endpoints require a JSON
    // payload (e.g. DELETE /auth/mfa/disable reads re-auth password/code from
    // the body). When `body` is undefined, makeRequest() sends no body and
    // strips Content-Type — identical to the previous bodyless behavior.
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'DELETE',
        headers: deleteHeaders,
        body,
      },
      skipAuth,
    )
  }

  // File upload helper.
  //
  // Issue #333 deep-review — `uploadFile()` used to throw on 401 without
  // attempting a token refresh and without emitting `session-expired`. A
  // user uploading a CSV after an idle session would see the upload fail
  // silently (no toast, no redirect), reproducing the exact symptom the
  // issue originally reported but only for file uploads. We mirror the
  // refresh-then-emit pattern from `makeRequest()` here: refresh once,
  // retry the upload with the new token, and fall through to
  // `clearAuthLocally()` + `emitSessionExpired()` on refresh failure.
  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, unknown>,
    isRetryAfterRefresh: boolean = false,
  ): Promise<ApiResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, JSON.stringify(value))
      })
    }

    const headers = { ...this.defaultHeaders }
    delete headers['Content-Type'] // Let browser set it for multipart/form-data

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.defaultTimeout)

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      // Handle responses without content (204 No Content) for upload method
      const data = await readJsonBody(response, 'JSON response in upload')

      if (!response.ok) {
        // Issue #333 deep-review — 401 on upload: attempt one refresh, retry
        // once with the new access token; only when refresh itself fails do
        // we surface the toast + redirect via SessionExpiredHandler. Skip
        // when the request is already a retry (avoid loop) or targets an
        // auth endpoint that must never trigger refresh.
        if (
          response.status === 401 &&
          !isRetryAfterRefresh &&
          !isAuthEndpointPathThatMustNotTriggerRefresh(endpoint) &&
          !isRejectedCredentialResponse(data) &&
          !this.endImpersonationIfItsTokenWasRejected()
        ) {
          if (await this.renewSessionAfter401(data)) {
            return this.uploadFile<T>(
              endpoint,
              file,
              additionalData,
              /* isRetryAfterRefresh */ true,
            )
          }
        }

        // Handle backend error structure: { success: false, error: { code, message, ... } }
        // Guard against null data (e.g. unexpected 204 on error path)
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- untrusted parsed JSON: a falsy non-object `error` field must fall through to the envelope
        const errorData = data?.error || data || { message: 'Unknown error' }
        const error: ApiError = {
          status: response.status,
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string message means unset and must fall through to statusText
          message: errorData.message || data?.message || response.statusText,
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset
          code: errorData.code || data?.code,
          // Same rule as makeRequest(): only real backend `details` (or the
          // `context` payload) — never the envelope object itself, which
          // downstream form mapping would misread as a field→message map.
          details: errorData.details ?? errorData.context,
          ...(typeof errorData.requestId === 'string' ? { requestId: errorData.requestId } : {}),
        }
        if (isMaintenanceRefusal(error)) emitMaintenance(noticeFromRefusal(error))
        throw new ApiException(error)
      }

      return {
        data: data?.data ?? null, // Extract the actual data from server response, handle null for 204 responses
        success: true,
        message: data?.message ?? undefined,
      }
    } catch (error) {
      clearTimeout(timeoutId)

      if (error instanceof ApiException) {
        throw error
      }

      // Timeout via AbortController
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiException({
          status: 0,
          code: 'TIMEOUT',
          message: '',
        })
      }

      // Transport/network failure (fetch() rejects with TypeError on CORS/offline/DNS)
      if (error instanceof TypeError) {
        throw new ApiException({
          status: 0,
          code: 'NETWORK_ERROR',
          message: '',
        })
      }

      const apiError = handleApiError(error)
      throw new ApiException(apiError)
    }
  }
}

// Export the class
export { ApiService }

// Create singleton instance
export const apiService = new ApiService()

// Re-export the api-helper compatibility layer from its own module so
// existing import sites (`from '@/platform/api/api.service'`) keep working
// without needing to update every consumer.
export { apiCall, api, unwrapApiResponse, throwBackendError } from './api-helper'
